import React, { useEffect, useMemo, useState } from "react";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import Swal from "sweetalert2";
import "./AsignarUnidadModal.scss";

export default function AsignarUnidadModal({
    visible,
    onHide,
    onApply,
    unidades = [],
    initialSelected = [],
    promoId = null,
    promoName = "promocion actual"
}) {
    const [filtro, setFiltro] = useState("");
    const [selectedIds, setSelectedIds] = useState([]);
    const [filtroTipo, setFiltroTipo] = useState("todas");

    useEffect(() => {
        if (visible) {
            setSelectedIds((initialSelected || []).map((id) => Number(id)));
            setFiltro("");
        }
    }, [visible, initialSelected]);

    const unidadesFiltradas = useMemo(() => {
        const term = (filtro || "").trim().toLowerCase();

        return (unidades || []).filter((unidad) => {
            if (term) {
                const matchText =
                    String(unidad.code).includes(term) ||
                    String(unidad.PROMO ?? "").includes(term) ||
                    (unidad.name || "").toLowerCase().includes(term);
                if (!matchText) return false;
            }

            const unitId = Number(unidad.code);
            const isSelected = selectedIds.includes(unitId);
            const hasPromo = unidad.PROMO !== null;

            if (filtroTipo === "seleccionadas") return isSelected;
            if (filtroTipo === "no_asignadas") return !isSelected;
            if (filtroTipo === "desocupadas") return !hasPromo;
            if (filtroTipo === "ocupadas") return hasPromo;
            return true;
        });
    }, [filtro, filtroTipo, unidades, selectedIds]);

    const notifyPending = (message) => {
        Swal.fire({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 1600,
            timerProgressBar: true,
            icon: "info",
            title: message
        });
    };

    const onToggleUnit = async (unidad) => {
        const unitId = Number(unidad.code);
        const isSelected = selectedIds.includes(unitId);
        const unitPromoId = unidad.PROMO === null ? null : Number(unidad.PROMO);
        const isCurrentPromo = unitPromoId !== null && Number(promoId) === unitPromoId;

        if (isSelected) {
            if (isCurrentPromo) {
                const confirm = await Swal.fire({
                    icon: "warning",
                    title: "Quitar unidad",
                    text: `Deseas quitar ${unidad.name} de la promo ${unitPromoId}?`,
                    showCancelButton: true,
                    confirmButtonText: "Si, quitar",
                    cancelButtonText: "Cancelar"
                });

                if (!confirm.isConfirmed) {
                    return;
                }

                notifyPending(`Se quitara de la promo ${unitPromoId} (pendiente)`);
            }

            setSelectedIds((prev) => prev.filter((id) => id !== unitId));
            return;
        }

        setSelectedIds((prev) => [...prev, unitId]);

        if (unitPromoId === null) {
            notifyPending(`Se asigno a ${promoName} (pendiente)`);
            return;
        }

        if (Number(promoId) === unitPromoId) {
            notifyPending(`Ya pertenece a la promo ${unitPromoId}`);
            return;
        }

        notifyPending(`Se asigno a ${promoName} (pendiente), antes estaba en promo ${unitPromoId}`);
    };

    const estadoUnidad = (unidad) => {
        const unitId = Number(unidad.code);
        const isSelected = selectedIds.includes(unitId);
        const unitPromoId = unidad.PROMO === null ? null : Number(unidad.PROMO);
        const isCurrentPromo = unitPromoId !== null && Number(promoId) === unitPromoId;

        if (isSelected && isCurrentPromo) {
            return { label: "Asignada a esta promo", cls: "current" };
        }
        if (!isSelected && isCurrentPromo) {
            return { label: "Se quitara de esta promo (pendiente)", cls: "remove" };
        }
        if (isSelected && unitPromoId !== null && !isCurrentPromo) {
            return { label: `Se asignara a esta promo (pendiente) — estaba en promo ${unitPromoId}`, cls: "takeover" };
        }
        if (isSelected && unitPromoId === null) {
            return { label: "Se asignara a esta promo (pendiente)", cls: "assign" };
        }
        if (unitPromoId === null) {
            return { label: "Sin promo", cls: "free" };
        }
        return { label: `Actualmente en promo ${unitPromoId}`, cls: "other" };
    };

    const payload = useMemo(() => {
        return {
            id: promoId,
            units: selectedIds.map((id) => Number(id))
        };
    }, [promoId, selectedIds]);

    return (
        <Dialog
            header="Asignar Unidad"
            visible={visible}
            onHide={onHide}
            modal
            style={{ width: "min(960px, 96vw)" }}
        >
            <div className="p-fluid">
                <div className="asignar-unidad__promo-info">
                    <i className="pi pi-tag" />
                    <strong>Promo actual:</strong>
                    <span className="asignar-unidad__promo-badge">
                        {promoId === null ? "Nueva promocion" : `${promoName} — ID ${promoId}`}
                    </span>
                </div>

                <InputText
                    className="asignar-unidad__search"
                    value={filtro}
                    onChange={(e) => setFiltro(e.target.value)}
                    placeholder="Buscar por id, nombre o promo…"
                />

                <div className="asignar-unidad__filters">
                    {[
                        { key: "todas",        label: "Todas" },
                        { key: "seleccionadas",label: "Seleccionadas" },
                        { key: "no_asignadas", label: "No asignadas" },
                        { key: "desocupadas",  label: "Sin promo" },
                        { key: "ocupadas",     label: "Con promo" },
                    ].map(({ key, label }) => (
                        <button
                            key={key}
                            type="button"
                            className={`asignar-unidad__filter-btn${
                                filtroTipo === key ? " asignar-unidad__filter-btn--active" : ""
                            }`}
                            onClick={() => setFiltroTipo(key)}
                        >
                            {label}
                        </button>
                    ))}
                </div>

                <div className="asignar-unidad__list">
                    {unidadesFiltradas.map((unidad) => {
                        const checked = selectedIds.includes(Number(unidad.code));
                        const estado = estadoUnidad(unidad);
                        const unitPromoId = unidad.PROMO === null ? null : Number(unidad.PROMO);
                        const isCurrentPromo = unitPromoId !== null && Number(promoId) === unitPromoId;
                        const isRemoving = !checked && isCurrentPromo;

                        return (
                            <label
                                key={unidad.code}
                                className={`asignar-unidad__item${
                                    checked ? " asignar-unidad__item--selected" : ""
                                }${isRemoving ? " asignar-unidad__item--removing" : ""}`}
                            >
                                <input
                                    className="asignar-unidad__item-checkbox"
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => onToggleUnit(unidad)}
                                />
                                <div className="asignar-unidad__item-info">
                                    <span className="asignar-unidad__item-name">
                                        {unidad.name} &nbsp;<span style={{ fontWeight: 400, color: "#64748b" }}>({unidad.code})</span>
                                    </span>
                                    <span className={`asignar-unidad__item-status asignar-unidad__item-status--${estado.cls}`}>
                                        {estado.label}
                                    </span>
                                </div>
                            </label>
                        );
                    })}
                </div>

                {/* <div className="asignar-unidad__payload">
                    <div className="asignar-unidad__payload-title">Payload — updateUnitsWhithPromo</div>
                    <pre>{JSON.stringify(payload, null, 2)}</pre>
                </div> */}
            </div>

            <div className="asignar-unidad__footer">
                <Button label="Cancelar" className="p-button-text" onClick={onHide} />
                <Button
                    label="Aplicar"
                    icon="pi pi-check"
                    onClick={() => {
                        onApply(payload);
                    }}
                />
            </div>
        </Dialog>
    );
}
